<script setup lang="ts">
// Mega footer: brand + payments, then Shop / Platforms / Account / Legal
// columns, then the legal line with the company record from the CMS.
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import BrandLogo from './BrandLogo.vue';
import BrandMark from './BrandMark.vue';
import { useAuthStore } from '../stores/auth';
import { useCompanyInfo } from '../composables/useCompanyInfo';
import { fetchSocialLinks, type SocialLink } from '../api/cms';
import { socialIcon } from '../utils/brand-icons';
import { PLATFORMS, platformLink } from '../data/platforms';
import { PRICE_RANGES, priceRangeLink } from '../data/price-ranges';

const { t } = useI18n();
const auth = useAuthStore();
const year = new Date().getFullYear();
const { company } = useCompanyInfo();
const socialLinks = ref<SocialLink[]>([]);

onMounted(async () => {
  try {
    socialLinks.value = await fetchSocialLinks();
  } catch {
    /* footer simply shows no socials */
  }
});

const companyName = computed(() => company.value?.name || 'GAMZOR');
const addressLines = computed(() => {
  const c = company.value;
  if (!c) return [];
  return [c.addressLine1, [c.postalCode, c.city].filter(Boolean).join(' '), c.country].filter(
    (l): l is string => Boolean(l),
  );
});

const menus = computed(() => [
  {
    heading: t('footer.shop'),
    links: [
      { to: '/catalog', label: t('nav.allGames') },
      { to: '/catalog?sort=popular', label: t('home.rows.bestsellers') },
      { to: '/catalog?sort=price_asc', label: t('nav.deals') },
      { to: '/catalog?sort=newest', label: t('nav.newReleases') },
      { to: '/catalog?q=card', label: t('nav.giftCards') },
      ...PRICE_RANGES.map((r) => ({ to: priceRangeLink(r), label: t(`home.price.${r.key}`) })),
    ],
  },
  {
    heading: t('footer.platforms'),
    links: PLATFORMS.map((p) => ({ to: platformLink(p.facets), label: p.name })),
  },
  {
    heading: t('footer.account'),
    links: auth.isAuthenticated
      ? [
          { to: '/account', label: t('nav.account') },
          { to: '/orders', label: t('nav.orders') },
          { to: '/account/billing', label: t('nav.billingWallet') },
          { to: '/favorites', label: t('nav.favourites') },
          { to: '/account/settings', label: t('nav.settings') },
        ]
      : [
          { to: '/login', label: t('nav.logIn') },
          { to: '/register', label: t('nav.signUp') },
          { to: '/account/billing', label: t('nav.billingWallet') },
        ],
  },
  {
    heading: t('footer.legal'),
    links: [
      { to: '/help', label: t('footer.linkHelp') },
      { to: '/articles', label: t('footer.linkArticles') },
      { to: '/legal/terms', label: t('footer.linkTerms') },
      { to: '/legal/privacy', label: t('footer.linkPrivacy') },
      { to: '/legal/cookies', label: t('footer.linkCookies') },
    ],
  },
]);
</script>

<template>
  <footer class="relative mt-20 overflow-hidden border-t border-white/5 bg-surface/80 backdrop-blur">
    <div class="pointer-events-none absolute inset-x-0 top-0 h-px bg-spectrum" aria-hidden="true"></div>
    <div class="pointer-events-none absolute -right-40 -top-40 size-[520px] rounded-full bg-featured/15 blur-[120px]" aria-hidden="true"></div>
    <div class="pointer-events-none absolute -bottom-40 -left-40 size-[420px] rounded-full bg-primary/15 blur-[120px]" aria-hidden="true"></div>

    <div class="mx-auto grid w-full max-w-[1280px] gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.5fr_repeat(4,1fr)] lg:gap-8">
      <!-- Brand column -->
      <div class="flex flex-col gap-5">
        <RouterLink to="/" class="inline-flex w-fit" :aria-label="t('footer.home')">
          <BrandLogo size="sm" />
        </RouterLink>
        <p class="max-w-[34ch] text-[14px] leading-relaxed text-text-dim">{{ t('footer.blurb') }}</p>

        <div v-if="socialLinks.length" class="flex items-center gap-1">
          <a
            v-for="link in socialLinks"
            :key="link.id"
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="link.platform"
            class="grid size-9 place-items-center rounded-md border border-border bg-bg text-text-dim transition-colors hover:border-text-mute hover:text-text"
          >
            <BrandMark v-if="socialIcon(link.platform)" :icon="socialIcon(link.platform)!" class="size-4" />
            <span v-else class="text-[10px] font-semibold uppercase">{{ link.platform.slice(0, 2) }}</span>
          </a>
        </div>
      </div>

      <!-- Link columns -->
      <nav v-for="menu in menus" :key="menu.heading" class="flex flex-col gap-3" :aria-label="menu.heading">
        <p class="font-display text-[13px] font-semibold text-text">{{ menu.heading }}</p>
        <ul class="flex flex-col gap-2">
          <li v-for="link in menu.links" :key="link.to + link.label">
            <RouterLink :to="link.to" class="text-[14px] text-text-dim transition-colors hover:text-text">{{ link.label }}</RouterLink>
          </li>
        </ul>
      </nav>
    </div>

    <div class="border-t border-border-faint">
      <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-2 px-4 py-5 text-[12px] text-text-mute sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <p>{{ t('footer.rights', { year }) }}</p>
        <address v-if="company" class="not-italic">
          <span class="text-text-dim">{{ companyName }}</span><template v-if="company.registrationNumber">, {{ t('footer.regNumber', { number: company.registrationNumber }) }}</template><template v-if="addressLines.length"> · {{ addressLines.join(', ') }}</template><template v-if="company.email"> · <a :href="'mailto:' + company.email" class="hover:text-text">{{ company.email }}</a></template>
        </address>
      </div>
    </div>
  </footer>
</template>
